import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import stringify from 'fast-json-stable-stringify';
import { mergeMap, of, sampleTime } from 'rxjs';
import { buffer, catchError, switchMap } from 'rxjs/operators';

import { UserActions } from '@recruitee/account-data';
import { CandidateActions } from '@recruitee/candidate-data';
import type { InterviewResultRequestReferences } from '@recruitee/interview-result-request-types';
import { InterviewTemplateActions } from '@recruitee/interview-templates-data';
import { objectToScope, referencesToActionsFactory } from '@recruitee/ngrx';
import { JobsBriefActions, StageActions } from '@recruitee/offers-data';

import {
  FetchParams,
  InterviewResultRequestHttpClient,
} from './interview-result-request-http-client';
import { InterviewResultRequestActions } from './interview-result-request.store';

@Injectable({ providedIn: 'root' })
export class InterviewResultRequestEffects {
  public create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InterviewResultRequestActions.createOnePessimistic),
      switchMap(({ payload }) =>
        this.interviewResultRequestHttpClient
          .create(payload.candidateId, payload.data, payload.shareOnTheGoRequestPayload)
          .pipe(
            switchMap(({ interviewResultRequests, meta, references }) => [
              ...this.getReferencesActions(references),
              InterviewResultRequestActions.createOnePessimisticSuccess({
                payload: {
                  data: interviewResultRequests[0],
                  correlationId: payload.correlationId,
                  ...(payload.shareOnTheGoRequestPayload?.grantAccess
                    ? { grantAccess: payload.shareOnTheGoRequestPayload.grantAccess }
                    : {}),
                },
              }),
            ]),
            catchError(error =>
              of(
                InterviewResultRequestActions.createOnePessimisticFailed({
                  payload: { error, correlationId: payload.correlationId },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  public fetchCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InterviewResultRequestActions.fetchCollection),
      switchMap(({ payload }) =>
        this.interviewResultRequestHttpClient.fetchCollection(payload.params).pipe(
          switchMap(({ interviewResultRequests, meta, references }) => [
            ...this.getReferencesActions(references),
            InterviewResultRequestActions.fetchCollectionSuccess({
              payload: {
                data: interviewResultRequests,
                meta,
                scope: objectToScope(payload.params),
              },
            }),
          ]),
          catchError(error =>
            of(
              InterviewResultRequestActions.fetchCollectionFailed({
                payload: { error, scope: objectToScope(payload.params) },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public fetchCollectionBuffered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InterviewResultRequestActions.fetchCollectionBuffered),
      buffer(
        this.actions$.pipe(
          ofType(InterviewResultRequestActions.fetchCollectionBuffered),
          sampleTime(100),
        ),
      ),
      mergeMap(actions =>
        [...new Set(actions.map(({ payload }) => stringify(payload.params)))].map(
          json => <FetchParams>JSON.parse(json),
        ),
      ),
      mergeMap(fetchParams =>
        this.interviewResultRequestHttpClient.fetchCollection(fetchParams).pipe(
          switchMap(({ interviewResultRequests, meta, references }) => [
            ...this.getReferencesActions(references),
            InterviewResultRequestActions.fetchCollectionBufferedSuccess({
              payload: { data: interviewResultRequests, meta, scope: objectToScope(fetchParams) },
            }),
          ]),
          catchError(error =>
            of(
              InterviewResultRequestActions.fetchCollectionBufferedFailed({
                payload: { error, scope: objectToScope(fetchParams) },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InterviewResultRequestActions.removeOnePessimistic),
      switchMap(({ payload }) =>
        this.interviewResultRequestHttpClient.delete(<number>payload.data).pipe(
          switchMap(({ interviewResultRequest, references }) => [
            ...this.getReferencesActions(references),
            InterviewResultRequestActions.removeOnePessimisticSuccess({
              payload: { data: interviewResultRequest },
            }),
          ]),
          catchError(error =>
            of(
              InterviewResultRequestActions.removeOnePessimisticFailed({
                payload: { error, data: payload.data },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public dismiss$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InterviewResultRequestActions.dismiss),
      switchMap(({ payload }) =>
        this.interviewResultRequestHttpClient.dismiss(payload.id).pipe(
          switchMap(({ interviewResultRequest, references }) => [
            ...this.getReferencesActions(references),
            InterviewResultRequestActions.dismissSuccess({
              payload: { data: interviewResultRequest },
            }),
          ]),
          catchError(error =>
            of(InterviewResultRequestActions.dismissFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  public dismissAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InterviewResultRequestActions.dismissAll),
      switchMap(({ payload }) =>
        this.interviewResultRequestHttpClient.dismissAll(payload.ids).pipe(
          switchMap(({ interviewResultRequests, meta, references }) => [
            ...this.getReferencesActions(references),
            InterviewResultRequestActions.dismissAllSuccess({
              payload: { data: interviewResultRequests, meta },
            }),
          ]),
          catchError(error =>
            of(
              InterviewResultRequestActions.dismissAllFailed({
                payload: { error, ids: payload.ids },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public revert$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InterviewResultRequestActions.revert),
      switchMap(({ payload }) =>
        this.interviewResultRequestHttpClient.revert(payload.id).pipe(
          switchMap(({ interviewResultRequest, references }) => [
            ...this.getReferencesActions(references),
            InterviewResultRequestActions.revertSuccess({
              payload: { data: interviewResultRequest },
            }),
          ]),
          catchError(error =>
            of(InterviewResultRequestActions.revertFailed({ payload: { error, id: payload.id } })),
          ),
        ),
      ),
    ),
  );

  private getReferencesActions = referencesToActionsFactory<InterviewResultRequestReferences>()([
    { type: 'Admin', action: UserActions.references },
    { type: 'Offer', action: JobsBriefActions.references },
    { type: 'Stage', action: StageActions.references },
    { type: 'Candidate', action: CandidateActions.references },
    { type: 'InterviewTemplate', action: InterviewTemplateActions.references },
  ]);

  constructor(
    private actions$: Actions,
    private interviewResultRequestHttpClient: InterviewResultRequestHttpClient,
  ) {}
}
